// Save-intent (image intent) attribution derived from Redux state.
import { createSelector } from 'reselect';
import {
  ImageIntentGallery,
  NO_IMAGE_INTENT_GALLERY,
  imageIntentGalleryFromAdditionalData,
} from '../components/v3/shared/protectedImage/imageIntentGallery';
import {
  OverlayStyle,
  resolveOverlayStyle,
} from '../components/v3/shared/protectedImage/overlayStyles';

// The gallery the parent entered the current ordering flow with, as the
// explicit triple a save-intent context carries. useImageIntentFriction fills
// the same triple in by itself; this is for reports made outside the hook
// (native listeners on SweetAlert markup), which must name the gallery
// themselves or report none.
interface StateWithAdditionalData {
  additionalData?: Parameters<typeof imageIntentGalleryFromAdditionalData>[0];
}

export const selectImageIntentGallery = createSelector(
  (state: StateWithAdditionalData) => state.additionalData,
  (additionalData): ImageIntentGallery =>
    imageIntentGalleryFromAdditionalData(additionalData),
);

interface StateWithOrderImageIntent {
  v3Order?: {
    orders?: Record<
      string,
      {
        additionalData?: Parameters<
          typeof imageIntentGalleryFromAdditionalData
        >[0];
      }
    >;
  };
}

// Cart-edit screens display the named order's photos, which may belong to
// another child than the order last opened. A missing snapshot means no
// gallery, never permission to use unrelated global selection state.
export const selectOrderImageIntentGallery = createSelector(
  (state: StateWithOrderImageIntent, orderKey: string | null) =>
    orderKey ? state.v3Order?.orders?.[orderKey]?.additionalData : null,
  (additionalData): ImageIntentGallery =>
    imageIntentGalleryFromAdditionalData(additionalData),
);

// The gallery the key-entry previews belong to (the student-selection and
// add-student screens, right after a parent enters a key): the key the lookup
// returned with the previews (`bnlPreShoot.galleryPassword`), paired with the
// Pics gallery id the same lookup stored in additionalData, as submitGalleryKey
// pairs them. `dp2GalleryId` is explicitly null: one key-entry path (the
// skip-registration lookup) leaves a stale galleryThatInitiatedOrder in
// additionalData, and the hook's Redux default would pair this key with that
// DP2 id. Without a preview key, report no gallery rather than borrowing a
// potentially stale order from Redux. One definition for both screens.
interface StateWithPreviewLookup extends StateWithAdditionalData {
  bnlPreShoot?: { galleryPassword?: string | null } | null;
}

export const selectImageIntentPreviewGallery = createSelector(
  (state: StateWithPreviewLookup) => state.bnlPreShoot?.galleryPassword,
  (state: StateWithPreviewLookup) => state.additionalData?.picsGalleryId,
  (galleryPassword, picsGalleryId): Partial<ImageIntentGallery> =>
    galleryPassword
      ? {
          galleryKey: galleryPassword,
          picsGalleryId: picsGalleryId ?? null,
          dp2GalleryId: null,
        }
      : NO_IMAGE_INTENT_GALLERY,
);

// The studio's visible image protection (companies.image_protection_enabled,
// ENG-2594), hydrated into the company slice from the SPA bootstrap. Off
// unless the payload turned it on.
interface StateWithCompany {
  company?: {
    imageProtectionEnabled?: boolean;
    imageProtectionStyle?: string | null;
  } | null;
}

export const selectImageProtectionEnabled = (
  state: StateWithCompany,
): boolean => !!state.company?.imageProtectionEnabled;

// The studio's overlay style, a known preset (the default for anything else).
export const selectImageProtectionStyle = (
  state: StateWithCompany,
): OverlayStyle => resolveOverlayStyle(state.company?.imageProtectionStyle);
